/**
 * Info Modal (Colofon)
 *
 * Information about the Kaart van Mensenkennis project.
 */

import { useState, useEffect } from 'react';
import { X } from '@phosphor-icons/react';
import { COLORS } from '../../lib/constants';
import { modalStyles, getCloseButtonStyle } from './styles';

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const styles = {
  ...modalStyles,
  modal: {
    ...modalStyles.modal,
    maxWidth: '560px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionLast: {
    marginBottom: 0,
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: COLORS.graphite,
    marginBottom: '10px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    opacity: 0.6,
  },
  paragraph: {
    fontSize: '17px',
    color: COLORS.graphite,
    lineHeight: 1.6,
    margin: 0,
  },
  link: {
    color: COLORS.royalBlue,
    textDecoration: 'none',
  },
  techList: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '10px',
    marginTop: '10px',
  },
  techTag: {
    padding: '6px 12px',
    fontSize: '15px',
    backgroundColor: `${COLORS.graphite}08`,
    borderRadius: 0,
    color: COLORS.graphite,
  },
  inspiration: {
    fontStyle: 'italic' as const,
    opacity: 0.9,
  },
};

export function InfoModal({ isOpen, onClose }: InfoModalProps) {
  const [closeHovered, setCloseHovered] = useState(false);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>): void {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div style={styles.header}>
          <h2 id="modal-title" style={styles.title}>Over deze kaart</h2>
          <button
            style={getCloseButtonStyle(closeHovered)}
            onClick={onClose}
            aria-label="Sluiten"
            onMouseEnter={() => setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div style={styles.content}>
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Het project</h3>
            <p style={styles.paragraph}>
              De <strong>Kaart van Mensenkennis</strong> brengt organisaties in Nederland in kaart
              die werken aan participatie, dialoog en democratische vernieuwing. Van
              burgerberaden tot deep democracy, van omgevingsparticipatie tot facilitatietrainingen —
              dit overzicht helpt je de juiste partijen te vinden voor jouw vraagstuk.
            </p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Inspiratie</h3>
            <p style={{ ...styles.paragraph, ...styles.inspiration }}>
              Met dank aan{' '}
              <a
                href="https://startupmap.one/"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.link}
              >
                Jan-Willem Denys
              </a>
              {' '}voor de inspiratie achter dit project.
            </p>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Gebouwd door</h3>
            <p style={styles.paragraph}>
              Een initiatief van{' '}
              <a
                href="https://dembrane.com"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.link}
              >
                Dembrane
              </a>
              {' '}— wij maken tools waarmee organisaties écht kunnen luisteren,
              ook als er honderden mensen aan tafel zitten.
            </p>
          </div>

          <div style={styles.sectionLast}>
            <h3 style={styles.sectionTitle}>Technologie</h3>
            <p style={styles.paragraph}>
              Deze webapp is gebouwd met:
            </p>
            <div style={styles.techList}>
              <span style={styles.techTag}>React</span>
              <span style={styles.techTag}>TypeScript</span>
              <span style={styles.techTag}>D3.js</span>
              <span style={styles.techTag}>Vite</span>
              <span style={styles.techTag}>Notion API</span>
              <span style={styles.techTag}>Vercel</span>
            </div>
          </div>
        </div>

        <div style={styles.footer}>
          <button style={styles.submitButton} onClick={onClose}>
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}
